import { ChangeEvent, KeyboardEvent, MouseEvent, useEffect, useRef, useState } from 'react';
import { ActionIcon, Box, Popover, Textarea, TextInput, TextInputProps } from '@mantine/core';
import { RiExpandDiagonalLine } from 'react-icons/ri';
import StyledModal from '@/components/StyledModal/StyledModal.tsx';

const clickOutsideEvents = ['pointerdown', 'mousedown', 'touchstart'];

interface ExpandingInputCellProps extends Omit<TextInputProps, 'onChange' | 'value'> {
  value: string;
  header: string;
  onChange: (e: ChangeEvent<HTMLTextAreaElement>) => void;
  disabled?: boolean;
  inputAriaLabel?: string;
}

const ExpandingInputCell = ({
  value,
  header,
  onChange,
  disabled = false,
  inputAriaLabel,
  ...props
}: ExpandingInputCellProps) => {
  const triggerRef = useRef<HTMLInputElement>(null);
  const initialFocusRef = useRef<HTMLTextAreaElement>(null);
  const [popoverOpened, setPopoverOpened] = useState(false);
  const [modalOpened, setModalOpened] = useState(false);
  const [offsetY, setOffsetY] = useState(0);

  useEffect(() => {
    if (triggerRef.current) {
      const height = triggerRef.current.offsetHeight;
      setOffsetY(-height);
    }
  }, []);

  const openPopover = (e?: MouseEvent) => {
    e?.stopPropagation();
    if (!disabled) setPopoverOpened(true);
  };

  const closePopover = () => {
    triggerRef.current?.blur();
    setPopoverOpened(false);
  };

  const openModalFromPopover = () => {
    // close popover first to avoid layered focus traps, then open modal
    setPopoverOpened(false);
    // next tick ensures Popover unmounts before Modal mounts
    setTimeout(() => setModalOpened(true), 0);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if ((e.key === 'Enter' && !e.shiftKey && !e.altKey) || e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      closePopover();
    } else if (e.key === 'Tab') {
      e.preventDefault();
      closePopover();
      // Focus next focusable in document order
      setTimeout(() => {
        const focusable = Array.from(
          document.querySelectorAll<HTMLElement>(
            'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
          ),
        );
        const currentIndex = focusable.findIndex((el) => el === triggerRef.current);
        if (currentIndex > -1 && currentIndex < focusable.length - 1) {
          focusable[currentIndex + 1]?.focus();
        }
      }, 0);
    }
  };

  return (
    <>
      <Popover
        opened={popoverOpened}
        onChange={setPopoverOpened}
        onDismiss={closePopover}
        closeOnClickOutside
        clickOutsideEvents={clickOutsideEvents}
        position="bottom-start"
        shadow="md"
        offset={{ mainAxis: offsetY, crossAxis: 0 }}
        width={300}
      >
        <Popover.Target>
          <div
            onClick={openPopover}
            style={{ cursor: disabled ? 'not-allowed' : 'pointer', width: '100%' }}
          >
            <TextInput
              size="sm"
              variant="unstyled"
              {...props}
              aria-label={inputAriaLabel}
              value={value}
              readOnly
              disabled={disabled}
              ref={triggerRef}
              styles={{
                input: {
                  cursor: disabled ? 'not-allowed' : 'pointer',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  minHeight: '30px',
                  fontSize: '14px',
                },
                wrapper: { width: '100%' },
              }}
            />
          </div>
        </Popover.Target>

        <Popover.Dropdown p={0} style={{ minWidth: '300px' }}>
          <Box style={{ position: 'relative' }}>
            <Textarea
              aria-label={inputAriaLabel}
              value={value}
              onChange={onChange}
              size="md"
              autoFocus
              minRows={6}
              maxRows={20}
              ref={initialFocusRef}
              onBlur={closePopover}
              onKeyDown={handleKeyDown}
              autosize
              styles={{
                input: {
                  paddingRight: '28px', // room for the icon button
                  fontSize: '14px',
                  // minHeight: '120px',
                },
              }}
            />
            <ActionIcon
              aria-label="Open larger editor"
              variant="subtle"
              size="sm"
              style={{
                position: 'absolute',
                right: 8,
                top: 8,
              }}
              onMouseDown={(e) => e.preventDefault()} // keep focus in textarea until click
              onClick={(e) => {
                e.stopPropagation();
                openModalFromPopover();
              }}
            >
              <RiExpandDiagonalLine size={16} />
            </ActionIcon>
          </Box>
        </Popover.Dropdown>
      </Popover>

      <StyledModal
        opened={modalOpened}
        onClose={() => setModalOpened(false)}
        title={header}
        size="md"
      >
        <Textarea
          aria-label={inputAriaLabel}
          value={value}
          onChange={onChange}
          size="md"
          autoFocus
          autosize
          minRows={6}
          maxRows={20}
          h="100%"
        />
      </StyledModal>
    </>
  );
};

export default ExpandingInputCell;
